// src/pages/touchPoints/timeline/JourneyGallery.js
import React, { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Input, Button, Modal, message } from 'antd';
import { toUserMessages } from '../../apiService/blustreamApi';
import { useJourneyListQuery, useAttributesQuery, useSegmentsQuery, useTouchpointQueries, subJourneyCounts } from './data/queries';
import { useJourneyEditor } from './data/useJourneyEditor';
import { useProductOptions } from './data/journeyHooks';
import { buildProductById } from './segment/segmentRead';
import { metricsOf } from './fixtures/metrics';
import { hueOf } from './dims';
import SegmentBar from './nodes/SegmentBar';
import ReadOnlyNotice from './ReadOnlyNotice';
import JourneyEditorDrawer from './JourneyEditorDrawer';
import JourneyPermissionGate from './JourneyPermissionGate';
import { PERMISSION_JOURNEYS_CREATE } from '../../auth/permissions';
import './timeline.css';

const PILL = { LIVE: 'live', PAUSED: 'paused', MUTED: 'paused', DISABLED: 'off', ARCHIVED: 'off' };
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

export default function JourneyGallery() {
  const history = useHistory();
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const base = history.location.pathname.startsWith('/preview') ? '/preview/timeline' : '/journeys';
  const editor = useJourneyEditor({ open: creating, mode: 'create', journey: null, journeyId: null });

  const { data: journeys, isLoading, isError } = useJourneyListQuery();
  const { data: attributeById } = useAttributesQuery();
  const { data: productOptions = [] } = useProductOptions();
  const productById = buildProductById(productOptions);
  const segmentIds = useMemo(() => (journeys || []).map((j) => j.segment_id), [journeys]);
  const { data: segments } = useSegmentsQuery(segmentIds);

  // one touchpoints query per journey — fills counts and feeds the sub-journey derive
  const ids = useMemo(() => (journeys || []).map((j) => j.journey_id), [journeys]);
  const tpQueries = useTouchpointQueries(ids);
  // useQueries returns a new array every render, so key the memo on each query's data
  // version instead — otherwise every keystroke in search re-derives the sub-journey counts.
  const tpVersion = tpQueries.map((query) => query.dataUpdatedAt).join(',');
  const touchpointsById = useMemo(() => {
    const byId = {};
    ids.forEach((id, i) => { if (tpQueries[i] && tpQueries[i].data) byId[id] = tpQueries[i].data; });
    return byId;
  }, [ids, tpVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // background sub-journey counts: only once every journey's touchpoints + segments are present
  const subCounts = useMemo(() => {
    if (!journeys || !segments) return {};
    if (ids.some((id) => !touchpointsById[id])) return {};
    const withTps = journeys.map((j) => ({ ...j, touchpoints: touchpointsById[j.journey_id] || [] }));
    return subJourneyCounts(withTps, segments);
  }, [journeys, segments, ids, touchpointsById]);

  if (isLoading || !journeys) return <div className="jm-page">Loading…</div>;
  if (isError) return <div className="jm-page"><h2 className="jm-title">Journey Manager</h2><p className="jm-count">Couldn't load journeys.</p></div>;

  const list = journeys.filter((j) => j.name.toLowerCase().includes(q.toLowerCase()));
  const entryReached = (id) => {
    const tps = touchpointsById[id];
    if (!tps || !tps.length) return null;
    const entry = [...tps].sort((a, b) => a.ordinal - b.ordinal)[0];
    return metricsOf(entry.touchpoint_id).reached;
  };

  return (
    <div className="jm-page">
      <h2 className="jm-title">Journey Manager<ReadOnlyNotice /></h2>
      <p className="jm-count">{plural(journeys.length, 'journey')}</p>
      <div className="jm-toolbar">
        <Input placeholder="Search journeys…" value={q} onChange={(e) => setQ(e.target.value)} className="jm-search" />
        <JourneyPermissionGate permission={PERMISSION_JOURNEYS_CREATE}><Button type="primary" onClick={() => setCreating(true)}>+ New Journey</Button></JourneyPermissionGate>
      </div>
      <div className="jm-grid">
        {list.map((j) => {
          const off = !j.is_active || PILL[j.state_enum] === 'off';
          const tps = touchpointsById[j.journey_id];
          const subs = subCounts[j.journey_id];
          const reached = entryReached(j.journey_id);
          const meta = [
            j.channels.join(', '),
            tps ? plural(tps.length, 'touchpoint') : null,
            subs > 0 ? plural(subs, 'sub-journey') : null,
            reached != null ? `${reached} reached` : null,
          ].filter(Boolean).join(' · ');
          return (
            <div key={j.journey_id} className="jm-card" style={{ opacity: off ? 0.55 : 1 }}
              onClick={() => history.push(`${base}/${j.journey_id}`)}>
              <div className="jm-acc" style={{ background: hueOf(j.journey_id) }} />
              <div className="jm-body">
                <div className="jm-top">
                  <span className="jm-name">{j.name}</span>
                  <span className="jm-right">
                    <span className={`tl-pill ${PILL[j.state_enum] || 'off'}`}>
                      {j.state_enum.charAt(0) + j.state_enum.slice(1).toLowerCase()}
                    </span>
                  </span>
                </div>
                <div className="jm-meta">{meta}</div>
                <SegmentBar segment={(segments || []).find((s) => s.segment_id === j.segment_id) || null} attributeById={attributeById || (() => null)} productById={productById} showEmpty />
              </div>
            </div>
          );
        })}
      </div>
      <JourneyEditorDrawer
        open={creating}
        editor={editor}
        onRequestClose={() => {
          if (editor.dirty) {
            Modal.confirm({
              title: 'Discard changes?',
              content: 'Your unsaved changes to this journey will be lost.',
              okText: 'Discard', okType: 'danger', cancelText: 'Keep editing',
              onOk: () => setCreating(false),
            });
          } else {
            setCreating(false);
          }
        }}
        onSave={() => editor.save(
          (newId) => { setCreating(false); history.push(`/journeys/${newId}`); },
          (err) => message.error(toUserMessages(err).join(' ')),
        )}
        attributeById={() => null}
      />
    </div>
  );
}
